import { createTheme, DEFAULT_THEME, mergeMantineTheme, type MantineColorsTuple } from '@mantine/core';

/**
 * Supply X — Harsha Agencies
 * Design tokens extracted from Option A (stacked & scannable).
 *
 * Principles encoded here:
 *  - 14px base, Inter, tabular figures on every numeric column
 *  - 36px controls on desktop, 42px on mobile touch targets
 *  - radius md (8) for cards/inputs, sm (4) for badges
 *  - spacing scale 4 / 8 / 12 / 16 / 24 / 32
 *  - 1px borders instead of drop shadows (shadows only for overlays)
 */

// Primary — deep indigo. Ramp follows Mantine indigo; 7 is the brand step.
const indigo: MantineColorsTuple = [
  '#edf2ff', // 0  tinted surfaces, selected rows
  '#dbe4ff', // 1  borders on tinted surfaces, active nav icon fill
  '#bac8ff', // 2
  '#91a7ff', // 3
  '#748ffc', // 4
  '#5c7cfa', // 5
  '#4c6ef5', // 6
  '#3b5bdb', // 7  PRIMARY — buttons, links, active nav, focus ring
  '#364fc7', // 8  hover / pressed
  '#2f44a8', // 9
];

// Workflow-state ramps (only the steps the UI actually uses are tuned)
const teal: MantineColorsTuple = [
  '#e6fcf5', '#c3fae8', '#96f2d7', '#63e6be', '#38d9a9',
  '#20c997', '#12b886', '#0ca678', '#099268', '#087f5b',
];
const orange: MantineColorsTuple = [
  '#fff8f2', '#ffe8cc', '#ffd8a8', '#ffc078', '#ffa94d',
  '#ff922b', '#fd7e14', '#f76707', '#e8590c', '#d9480f',
];
const yellow: MantineColorsTuple = [
  '#fff9db', '#fff3bf', '#ffec99', '#ffe066', '#ffd43b',
  '#fcc419', '#fab005', '#f08c00', '#a1710a', '#7a5305',
];
const red: MantineColorsTuple = [
  '#fff5f5', '#ffe3e3', '#ffc9c9', '#ffa8a8', '#ff8787',
  '#ff6b6b', '#fa5252', '#e03131', '#c92a2a', '#a51111',
];
const blue: MantineColorsTuple = [
  '#e7f5ff', '#d0ebff', '#a5d8ff', '#74c0fc', '#4dabf7',
  '#339af0', '#228be6', '#1c7ed6', '#1971c2', '#1864ab',
];
const gray: MantineColorsTuple = [
  '#f8f9fa', // 0  app background / table zebra + computed columns
  '#f1f3f5', // 1  inner dividers, chip fills, group bands
  '#e9ecef', // 2  desk background, disabled fills, empty meters
  '#dee2e6', // 3  DEFAULT 1px border
  '#ced4da', // 4  input border, frame border
  '#adb5bd', // 5  disabled text, chevrons
  '#868e96', // 6  secondary text
  '#495057', // 7  body text on tinted ground
  '#343a40', // 8
  '#212529', // 9  primary text
];

/**
 * Workflow state → colour token. Never colour-only: pair with a label.
 *
 * Hoisted out of the theme object so `StateKey` below can be the exact union
 * of these keys. Inside `createTheme` the `other` field is optional, so
 * `keyof typeof theme.other.state` is not a usable type - and a state key
 * that is merely `string` is one a typo can invent at runtime.
 */
/**
 * Module accent → colour. A DIFFERENT vocabulary from STATE, deliberately.
 *
 * These were in STATE, beside the statuses, and that was wrong in a way a
 * test caught: StateBadge's rule is that every token it can render carries a
 * label, because colour must never be the only thing saying what a row is.
 * A module accent has no label to carry - it is not a claim about a record's
 * state, it is "which part of the business does this belong to", and the
 * module's name is always written next to it in full.
 *
 * Keeping them in one map meant either a guard that could no longer tell the
 * two apart, or inventing labels for tokens that are not badges. Two maps,
 * and each rule is exact:
 *
 *   STATE  - every token with a tint carries a label. Renders a badge.
 *   STREAM - every token carries a tint. Renders a border, a dot or a pill
 *            beside text that already names the module.
 *
 * `fg` is the 3px left border and the pill's text; `bg` the tint behind a
 * module card's icon square; `color`/`shade` the Mantine pair for a dot.
 */
export const STREAM = {
  sales:       { color: 'indigo', shade: 7, bg: '#edf2ff', fg: '#3b5bdb' },
  service:     { color: 'orange', shade: 7, bg: '#fff8f2', fg: '#e8590c' },
  inventory:   { color: 'teal',   shade: 7, bg: '#e6fcf5', fg: '#087f5b' },
  delivery:    { color: 'blue',   shade: 7, bg: '#e7f5ff', fg: '#1864ab' },
  collections: { color: 'green',  shade: 8, bg: '#ebfbee', fg: '#2b8a3e' },
  marketing:   { color: 'grape',  shade: 7, bg: '#f8f0fc', fg: '#9c36b5' },
  admin:       { color: 'gray',   shade: 7, bg: '#f1f3f5', fg: '#495057' },
  /** Not a module - the finance stream on a dashboard tile. */
  finance:     { color: 'gray',   shade: 6, bg: '#f1f3f5', fg: '#495057' },
} as const;

export type StreamKey = keyof typeof STREAM;

export const STATE = {
    // service pipeline
    received:   { color: 'gray',   shade: 6, bg: '#f1f3f5', fg: '#495057', label: 'Received' },
    diagnosed:  { color: 'indigo', shade: 7, bg: '#edf2ff', fg: '#3b5bdb', label: 'Diagnosed' },
    inRepair:   { color: 'orange', shade: 7, bg: '#fff8f2', fg: '#e8590c', label: 'Under Repair' },
    ready:      { color: 'teal',   shade: 7, bg: '#e6fcf5', fg: '#087f5b', label: 'Ready' },
    delivered:  { color: 'gray',   shade: 5, bg: '#f8f9fa', fg: '#868e96', label: 'Delivered' },
    // exceptions
    awaitingParts: { color: 'yellow', shade: 7, bg: '#fff9db', fg: '#a1710a', label: 'Awaiting parts' },
    overdue:       { color: 'red',    shade: 7, bg: '#fff5f5', fg: '#c92a2a', label: 'Overdue' },
    unreconciled:  { color: 'red',    shade: 7, bg: '#fff5f5', fg: '#c92a2a', label: 'Unreconciled' },
    awaitingApproval: { color: 'yellow', shade: 7, bg: '#fff9db', fg: '#a1710a', label: 'Awaiting approval' },
    queuedOffline: { color: 'yellow', shade: 7, bg: '#fff9db', fg: '#a1710a', label: 'Queued' },
    // deltas
    positive: { color: 'teal', shade: 9, fg: '#087f5b' },
    negative: { color: 'red',  shade: 9, fg: '#c92a2a' },

    // ----------------------------------------------------------------
    // SUPPLY X'S OWN STATUSES
    //
    // Added during the UI retrofit. The entries above are the design's
    // illustrative vocabulary - "Under Repair", "Awaiting parts" - and this
    // app's lifecycle is not that one. StateBadge is driven ENTIRELY by
    // this map and never accepts a raw colour, so every status a screen can
    // render has to exist here or it cannot be shown at all. That is the
    // point: it is what stops a screen inventing a colour.
    //
    // The vocabulary above is reused deliberately, so the two agree: gray
    // for a neutral waiting state, indigo for a decision recorded, teal for
    // finished, yellow for "somebody must act", red for a failure, blue for
    // in transit. Colour NEVER carries meaning alone - every entry has a
    // label, and every badge renders it.
    // ----------------------------------------------------------------

    // Service job (SERVICE_JOB_STATUSES)
    svcCollected: { color: 'blue',   shade: 7, bg: '#e7f5ff', fg: '#1864ab', label: 'Collected' },
    svcReceived:  { color: 'gray',   shade: 6, bg: '#f1f3f5', fg: '#495057', label: 'At the workshop' },
    svcDiagnosed: { color: 'indigo', shade: 7, bg: '#edf2ff', fg: '#3b5bdb', label: 'Diagnosed' },
    // Two finished states, and the difference is worth a colour: a repaired
    // battery owes nothing, a replacement has an order somebody must bill.
    svcServiced:  { color: 'teal',   shade: 7, bg: '#e6fcf5', fg: '#087f5b', label: 'OK — ready to go back' },
    svcReplaced:  { color: 'indigo', shade: 7, bg: '#edf2ff', fg: '#3b5bdb', label: 'Replaced' },
    svcRejected:  { color: 'red',    shade: 7, bg: '#fff5f5', fg: '#c92a2a', label: 'Rejected' },
    svcDelivered: { color: 'gray',   shade: 5, bg: '#f8f9fa', fg: '#868e96', label: 'Delivered back' },
    svcCancelled: { color: 'gray',   shade: 5, bg: '#f8f9fa', fg: '#868e96', label: 'Cancelled' },

    // Delivery task (DELIVERY_STATUSES)
    dlvPending:   { color: 'yellow', shade: 7, bg: '#fff9db', fg: '#a1710a', label: 'Waiting for an agent' },
    dlvAssigned:  { color: 'blue',   shade: 7, bg: '#e7f5ff', fg: '#1864ab', label: 'Out for delivery' },
    dlvDelivered: { color: 'teal',   shade: 7, bg: '#e6fcf5', fg: '#087f5b', label: 'Delivered' },
    dlvFailed:    { color: 'red',    shade: 7, bg: '#fff5f5', fg: '#c92a2a', label: 'Attempt failed' },

    // Order (ORDER_STATUSES)
    ordCreated:        { color: 'gray',   shade: 6, bg: '#f1f3f5', fg: '#495057', label: 'Created' },
    ordReadyToBill:    { color: 'yellow', shade: 7, bg: '#fff9db', fg: '#a1710a', label: 'Ready to bill' },
    ordBilled:         { color: 'indigo', shade: 7, bg: '#edf2ff', fg: '#3b5bdb', label: 'Billed' },
    ordOutForDelivery: { color: 'blue',   shade: 7, bg: '#e7f5ff', fg: '#1864ab', label: 'Out for delivery' },
    ordDelivered:      { color: 'gray',   shade: 5, bg: '#f8f9fa', fg: '#868e96', label: 'Delivered' },
    ordCancelled:      { color: 'gray',   shade: 5, bg: '#f8f9fa', fg: '#868e96', label: 'Cancelled' },

    // Day order (PRE_ORDER_STATUSES)
    preOrderPending:   { color: 'gray',   shade: 6, bg: '#f1f3f5', fg: '#495057', label: 'Pending' },
    preOrderPartial:   { color: 'yellow', shade: 7, bg: '#fff9db', fg: '#a1710a', label: 'Part fulfilled' },
    preOrderConverted: { color: 'teal',   shade: 7, bg: '#e6fcf5', fg: '#087f5b', label: 'Converted' },
    preOrderCancelled: { color: 'gray',   shade: 5, bg: '#f8f9fa', fg: '#868e96', label: 'Cancelled' },
  } as const;

export type StateKey = keyof typeof STATE;

/**
 * The subset of states that can be rendered as a BADGE.
 *
 * The map holds two different kinds of thing: workflow states, which carry a
 * background, a foreground and a label; and stream/delta tokens, which carry
 * only a colour and a shade because they tint a dot or a figure rather than
 * filling a pill.
 *
 * Passing a stream to StateBadge would render an empty badge with no label -
 * so the type refuses it. The compiler found this the moment StateBadge was
 * first written, which is the argument for typing theme.other at all.
 */
export type BadgeStateKey = {
  [K in StateKey]: (typeof STATE)[K] extends { bg: string; fg: string; label: string }
    ? K
    : never;
}[StateKey];

export const supplyXTheme = createTheme({
  primaryColor: 'indigo',
  primaryShade: { light: 7, dark: 7 },
  colors: { indigo, teal, orange, yellow, red, blue, gray },

  white: '#ffffff',
  black: '#212529',

  fontFamily: 'Inter, -apple-system, Segoe UI, Roboto, sans-serif',
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  // Tabular figures everywhere; numeric columns additionally get `.num`.
  fontSmoothing: true,

  fontSizes: {
    xs: '11px', // badges, serial numbers, micro-meta
    sm: '12px', // secondary meta, table column headers
    md: '14px', // BASE — body, inputs, buttons
    lg: '15px', // card titles, list primary text
    xl: '18px', // section titles
  },
  lineHeights: {
    xs: '1.3',
    sm: '1.4',
    md: '1.45',
    lg: '1.4',
    xl: '1.3',
  },
  headings: {
    fontFamily: 'Inter, -apple-system, Segoe UI, Roboto, sans-serif',
    fontWeight: '600',
    sizes: {
      h1: { fontSize: '32px', lineHeight: '1.1', fontWeight: '600' }, // hero metric
      h2: { fontSize: '26px', lineHeight: '1.15', fontWeight: '600' }, // KPI tile
      h3: { fontSize: '18px', lineHeight: '1.25', fontWeight: '600' }, // page title
      h4: { fontSize: '16px', lineHeight: '1.3', fontWeight: '600' },  // screen title
      h5: { fontSize: '15px', lineHeight: '1.35', fontWeight: '600' }, // card title
      h6: { fontSize: '12px', lineHeight: '1.3', fontWeight: '700' },  // uppercase eyebrow
    },
  },

  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    // 32px = `xxl` via theme.other.spacingXxl (Mantine stops at xl)
  },

  radius: {
    xs: '2px', // meters, progress rails
    sm: '4px', // badges, chips, small buttons
    md: '8px', // DEFAULT — cards, inputs, buttons, sheets
    lg: '12px',
    xl: '16px',
  },
  defaultRadius: 'md',

  // Borders carry the structure; shadows are reserved for things that float.
  shadows: {
    xs: 'none',
    sm: 'none',
    md: '0 2px 8px rgba(33, 37, 41, 0.08)',   // popovers, dropdowns
    lg: '0 -8px 24px rgba(33, 37, 41, 0.12)', // bottom sheets (action sheet)
    xl: '0 -8px 24px rgba(33, 37, 41, 0.18)', // modal sheets over a dimmer
  },

  focusRing: 'auto',
  cursorType: 'pointer',

  other: {
    spacingXxl: '32px',
    controlHeight: 36,        // desktop / default
    controlHeightTouch: 42,   // mobile touch targets
    borderColor: '#dee2e6',
    borderColorInput: '#ced4da',
    appBackground: '#f8f9fa',
    surface: '#ffffff',
    tableRowHeight: 40,
    tableRowHeightComfortable: 48,
    bottomNavHeight: 58,
    dimmer: 'rgba(33, 37, 41, 0.45)',
    numericFontFeatures: '"tnum" 1',

    state: STATE,
  },

  components: {
    Button: {
      defaultProps: { size: 'sm', radius: 'md' }, // sm = 36px; use 'md' (42px) on mobile
      styles: { root: { fontWeight: 600 } },
    },
    ActionIcon: { defaultProps: { size: 36, radius: 'md', variant: 'default' } },
    TextInput: { defaultProps: { size: 'sm', radius: 'md' } },
    NumberInput: {
      // Numeric entry must open a number pad on Android.
      defaultProps: {
        size: 'sm',
        radius: 'md',
        inputMode: 'decimal',
        hideControls: true,
        thousandSeparator: ',',
      },
      styles: { input: { fontVariantNumeric: 'tabular-nums', fontWeight: 600 } },
    },
    Select: { defaultProps: { size: 'sm', radius: 'md' } },
    Textarea: { defaultProps: { size: 'sm', radius: 'md' } },
    Card: {
      defaultProps: { radius: 'md', padding: 'lg', withBorder: true, shadow: undefined },
    },
    Paper: { defaultProps: { radius: 'md', withBorder: true, shadow: undefined } },
    Badge: {
      defaultProps: { radius: 'sm', size: 'sm' },
      styles: { root: { fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase' } },
    },
    Chip: { defaultProps: { radius: 'sm', size: 'sm' } },
    Table: {
      defaultProps: { withTableBorder: true, withColumnBorders: false, verticalSpacing: 'sm', horizontalSpacing: 'md' },
      styles: {
        th: { fontSize: '12px', fontWeight: 600, color: '#495057', background: '#f8f9fa' },
        td: { fontSize: '13px' },
      },
    },
    Modal: { defaultProps: { radius: 'md', centered: true, overlayProps: { backgroundOpacity: 0.45, blur: 0 } } },
    Drawer: { defaultProps: { radius: 0, overlayProps: { backgroundOpacity: 0.45 } } },
    Tabs: { styles: { tab: { fontSize: '14px', height: 44 } } },
    Progress: { defaultProps: { radius: 'xs', size: 'sm' } },
    Divider: { defaultProps: { color: 'gray.3' } },
    Tooltip: { defaultProps: { radius: 'sm', withArrow: true } },
    Stepper: { defaultProps: { size: 'sm', radius: 'xl' } },
  },
});

export const theme = mergeMantineTheme(DEFAULT_THEME, supplyXTheme);

/**
 * Types for `theme.other`.
 *
 * Without this, `theme.other` is `Record<string, any>` and a typo in a state
 * key - `svcRecieved` - reads as undefined at runtime and renders a badge
 * with no colour and no label. Module augmentation makes it a compile error
 * instead, which is the whole reason StateBadge can promise it never shows a
 * raw colour.
 */
export interface StateToken {
  color: string;
  shade: number;
  bg?: string;
  fg?: string;
  label?: string;
}

declare module '@mantine/core' {
  export interface MantineThemeOther {
    spacingXxl: string;
    controlHeight: number;
    controlHeightTouch: number;
    borderColor: string;
    borderColorInput: string;
    appBackground: string;
    surface: string;
    tableRowHeight: number;
    tableRowHeightComfortable: number;
    bottomNavHeight: number;
    dimmer: string;
    numericFontFeatures: string;
    state: Record<string, StateToken>;
  }
}
